import { PayoutPreferences } from "@wingspanhq/payments/dist/interfaces";
import {
  PayoutDestinationType,
  PayoutSettingsUpdate,
  PayoutSettingsUpdateStandardDestination,
  PreferredPayoutSpeed
} from "@wingspanhq/wingspan-api-sdk";
import { MethodValue } from "../components/ManageMethods/types";
import { selectorMethodPayoutSpeed } from "./selectorMethodPayoutSpeed";

type Args = {
  method: MethodValue | null | undefined;
  platformBalanceAccountId?: string;
};

/**
 * Builds the `PATCH /payout-settings` body for the method the user picked in
 * the payout-method list. Returns `undefined` when the pick cannot be written —
 * nothing selected, no payout speed, or a method type that is not a payout
 * destination — so the caller can skip the write instead of sending a body the
 * API would reject.
 *
 * An internal account resolves to `platformBalanceAccountId`, not to the
 * selected row's own id: it is the destination id the payout settings already
 * carry. Selecting an internal account that is not that one clears the
 * destination, which is the behaviour this screen has always had.
 */
export function selectorPayoutSettingsUpdateForMethod({
  method,
  platformBalanceAccountId
}: Args): PayoutSettingsUpdate | undefined {
  if (!method) {
    return undefined;
  }

  const speed = selectorMethodPayoutSpeed(method);

  if (!speed) {
    return undefined;
  }

  const destination = resolveDestination(method, platformBalanceAccountId);

  if (destination === undefined) {
    return undefined;
  }

  return speed === PayoutPreferences.Instant
    ? {
        preferredPayoutSpeed: PreferredPayoutSpeed.INSTANT,
        instantDestination: destination
      }
    : {
        preferredPayoutSpeed: PreferredPayoutSpeed.STANDARD,
        standardDestination: destination
      };
}

function resolveDestination(
  method: MethodValue,
  platformBalanceAccountId?: string
): PayoutSettingsUpdateStandardDestination | null | undefined {
  switch (method.type) {
    case "InternalAccount":
      return platformBalanceAccountId
        ? {
            type: PayoutDestinationType.INTERNAL_ACCOUNT,
            id: platformBalanceAccountId
          }
        : null;

    case "Account":
      return {
        type: PayoutDestinationType.EXTERNAL_BANK_ACCOUNT,
        id: method.id
      };

    case "PaymentCard":
      return {
        type: PayoutDestinationType.PAYMENT_CARD,
        id: method.id
      };

    default:
      return undefined;
  }
}
